/**
 * Israeli localities, for the "city" field on an enquiry and on a quote.
 *
 * A plain text box put "תל אביב", "ת״א" and "תל-אביב יפו" in three different
 * records for one place, which is only a nuisance until someone searches for a
 * customer by where they live. Offering the names instead makes the common case
 * a tap and leaves the spelling consistent.
 *
 * This is the Central Bureau of Statistics' own register of localities, every
 * one of them — cities, local councils, moshavim, kibbutzim and Bedouin
 * villages alike. A hand-picked shortlist of the large towns was the wrong
 * shape for a trade business: the customers who need someone driven out to them
 * are exactly the ones living in the places a shortlist leaves out, and every
 * one of those addresses had to be typed from scratch.
 *
 * Still suggestions and not a closed set. A name spelled differently to the
 * register, a new neighbourhood, an address abroad — the field keeps whatever
 * is typed, because one that refuses an address it has not heard of is worse
 * than one that accepts a typo.
 *
 * Held in `shared` rather than in the web app because the API validates the
 * same field and the PDF prints it.
 *
 * Source: data.gov.il, "רשימת ישובים בישראל" (CBS). Regenerate by reading that
 * dataset again — it is a list of places, so it changes about as often.
 */
export const ISRAELI_LOCALITIES: readonly string[] = [
  'אבו ג\'ווייעד (שבט)',
  'אבו גוש',
  'אבו סנאן',
  'אבו סריחאן (שבט)',
  'אבו עבדון (שבט)',
  'אבו עמאר (שבט)',
  'אבו עמרה (שבט)',
  'אבו קורינאת (שבט)',
  'אבו קרינאת (יישוב)',
  'אבו רובייעה (שבט)',
  'אבו רוקייק (שבט)',
  'אבו תלול',
  'אבטין',
  'אבטליון',
  'אביאל',
  'אביבים',
  'אביגדור',
  'אביגיל',
  'אביה',
  'אביחיל',
  'אביטל',
  'אביעזר',
  'אבירים',
  'אבן יהודה',
  'אבן מנחם',
  'אבן ספיר',
  'אבן שמואל',
  'אבני איתן',
  'אבני חפץ',
  'אבשלום',
  'אדורה',
  'אדוריים',
  'אדירים',
  'אדמית',
  'אדרת',
  'אובנת',
  'אודים',
  'אודם',
  'אוהד',
  'אום אל-פחם',
  'אום אל-קוטוף',
  'אום בטין',
  'אומן',
  'אומץ',
  'אופקים',
  'אור הגנוז',
  'אור הנר',
  'אור יהודה',
  'אור עקיבא',
  'אורה',
  'אורות',
  'אורטל',
  'אורים',
  'אורנים',
  'אורנית',
  'אושה',
  'אזור',
  'אחווה',
  'אחוזם',
  'אחוזת ברק',
  'אחיה',
  'אחיהוד',
  'אחיטוב',
  'אחיסמך',
  'אחיעזר',
  'אטרש (שבט)',
  'איבי הנחל',
  'איבים',
  'אייל',
  'איילת השחר',
  'אילון',
  'אילות',
  'אילניה',
  'אילת',
  'אירוס',
  'איתמר',
  'איתן',
  'איתנים',
  'אכסאל',
  'אל סייד',
  'אל-עזי',
  'אל-עריאן',
  'אל-רום',
  'אלומה',
  'אלומות',
  'אלון',
  'אלון הגליל',
  'אלון מורה',
  'אלון שבות',
  'אלוני אבא',
  'אלוני הבשן',
  'אלוני יצחק',
  'אלונים',
  'אלי-עד',
  'אליאב',
  'אליכין',
  'אליפז',
  'אליפלט',
  'אליקים',
  'אלישיב',
  'אלישמע',
  'אלמגור',
  'אלמוג',
  'אלעד',
  'אלעזר',
  'אלפי מנשה',
  'אלקוש',
  'אלקנה',
  'אמונים',
  'אמירים',
  'אמנון',
  'אמציה',
  'אניעם',
  'אסד (שבט)',
  'אסיף',
  'אספר',
  'אעבלין',
  'אעצם (שבט)',
  'אפיניש (שבט)',
  'אפיק',
  'אפיקים',
  'אפק',
  'אפרת',
  'ארבל',
  'ארגמן',
  'ארז',
  'אריאל',
  'ארסוף',
  'אשבול',
  'אשבל',
  'אשדוד',
  'אשדות יעקב (איחוד)',
  'אשדות יעקב (מאוחד)',
  'אשחר',
  'אשכולות',
  'אשל הנשיא',
  'אשלים',
  'אשקלון',
  'אשרת',
  'אשתאול',
  'אתגר',
  'באקה אל-גרביה',
  'באר אורה',
  'באר גנים',
  'באר טוביה',
  'באר יעקב',
  'באר מילכה',
  'באר שבע',
  'בארות יצחק',
  'בארותיים',
  'בארי',
  'בוסתן הגליל',
  'בועיינה-נוג\'ידאת',
  'בוקעאתא',
  'בורגתה',
  'בחן',
  'בטחה',
  'ביצרון',
  'ביר אל-מכסור',
  'ביר הדאג\'',
  'ביריה',
  'בית אורן',
  'בית אל',
  'בית אלעזרי',
  'בית אלפא',
  'בית אריה',
  'בית ברל',
  'בית ג\'ן',
  'בית גוברין',
  'בית גמליאל',
  'בית דגן',
  'בית הגדי',
  'בית הלוי',
  'בית הלל',
  'בית העמק',
  'בית הערבה',
  'בית השיטה',
  'בית זיד',
  'בית זית',
  'בית זרע',
  'בית חוגלה',
  'בית חורון',
  'בית חירות',
  'בית חלקיה',
  'בית חנן',
  'בית חנניה',
  'בית חשמונאי',
  'בית יהושע',
  'בית יוסף',
  'בית ינאי',
  'בית יצחק-שער חפר',
  'בית יתיר',
  'בית לחם הגלילית',
  'בית מאיר',
  'בית נחמיה',
  'בית ניר',
  'בית נקופה',
  'בית עובד',
  'בית עוזיאל',
  'בית עזרא',
  'בית עריף',
  'בית צבי',
  'בית קמה',
  'בית קשת',
  'בית רבן',
  'בית רימון',
  'בית שאן',
  'בית שמש',
  'בית שערים',
  'בית שקמה',
  'ביתן אהרן',
  'ביתר עילית',
  'בלפוריה',
  'בן זכאי',
  'בן עמי',
  'בן שמן (כפר נוער)',
  'בן שמן (מושב)',
  'בני ברק',
  'בני דקלים',
  'בני דרום',
  'בני דרור',
  'בני יהודה',
  'בני נצרים',
  'בני עטרות',
  'בני עיש',
  'בני ציון',
  'בני ראם',
  'בניה',
  'בנימינה-גבעת עדה',
  'בסמה',
  'בסמת טבעון',
  'בענה',
  'בצרה',
  'בצת',
  'בקוע',
  'בקעות',
  'בר גיורא',
  'בר יוחאי',
  'בר כוכבא',
  'ברוכין',
  'ברור חיל',
  'ברוש',
  'ברוש הבקעה',
  'ברכה',
  'ברכיה',
  'ברעם',
  'ברק',
  'ברקאי',
  'ברקן',
  'ברקת',
  'בת הדר',
  'בת חן',
  'בת חפר',
  'בת ים',
  'בת עין',
  'בת שלמה',
  'בתרון',
  'ג\'דיידה-מכר',
  'ג\'ולס',
  'ג\'לג\'וליה',
  'ג\'נאביב (שבט)',
  'ג\'סר א-זרקא',
  'ג\'ש (גוש חלב)',
  'ג\'ת',
  'גאולי תימן',
  'גאולים',
  'גאליה',
  'גבולות',
  'גבים',
  'גבע',
  'גבע בנימין',
  'גבע כרמל',
  'גבעולים',
  'גבעון החדשה',
  'גבעות',
  'גבעות בר',
  'גבעות הרואה',
  'גבעות עדן',
  'גבעת אבני',
  'גבעת אלה',
  'גבעת ברנר',
  'גבעת השלושה',
  'גבעת זאב',
  'גבעת חיים (איחוד)',
  'גבעת חיים (מאוחד)',
  'גבעת חן',
  'גבעת יואב',
  'גבעת יערים',
  'גבעת ישעיהו',
  'גבעת כח',
  'גבעת נילי',
  'גבעת עוז',
  'גבעת שמואל',
  'גבעת שמש',
  'גבעת שפירא',
  'גבעתי',
  'גבעתיים',
  'גברעם',
  'גבת',
  'גדות',
  'גדיש',
  'גדעונה',
  'גדרה',
  'גודר',
  'גונן',
  'גורן',
  'גורנות הגליל',
  'גזית',
  'גזר',
  'גיאה',
  'גיבתון',
  'גיזו',
  'גילון',
  'גילת',
  'גינוסר',
  'גיניגר',
  'גינתון',
  'גיתה',
  'גיתית',
  'גלאון',
  'גלגל',
  'גליל ים',
  'גלעד (אבן יצחק)',
  'גמזו',
  'גן הדרום',
  'גן השומרון',
  'גן חיים',
  'גן יאשיה',
  'גן יבנה',
  'גן נר',
  'גן שורק',
  'גן שלמה',
  'גן שמואל',
  'גנות',
  'גנות הדר',
  'גני הדר',
  'גני טל',
  'גני יוחנן',
  'גני מודיעין',
  'גני עם',
  'גני תקווה',
  'גנים',
  'געש',
  'געתון',
  'גפן',
  'גרופית',
  'גשור',
  'גשר',
  'גשר הזיו',
  'גת (קיבוץ)',
  'גת רימון',
  'דאלית אל-כרמל',
  'דבורה',
  'דבוריה',
  'דביר',
  'דברת',
  'דגניה א\'',
  'דגניה ב\'',
  'דובב',
  'דולב',
  'דור',
  'דורות',
  'דחי',
  'דייר אל-אסד',
  'דייר חנא',
  'דייר ראפאת',
  'דימונה',
  'דישון',
  'דליה',
  'דלתון',
  'דמיידה',
  'דן',
  'דפנה',
  'דקל',
  'דריג\'את',
  'האון',
  'הבונים',
  'הגושרים',
  'הדר עם',
  'הוד השרון',
  'הודיה',
  'הודיות',
  'הוואשלה (שבט)',
  'הוזייל (שבט)',
  'הושעיה',
  'הזורע',
  'הזורעים',
  'החותרים',
  'היוגב',
  'הילה',
  'המעפיל',
  'הסוללים',
  'העוגן',
  'הר אדר',
  'הר גילה',
  'הר עמשא',
  'הראל',
  'הרדוף',
  'הרצליה',
  'הררית',
  'ורד יריחו',
  'ורדון',
  'זבארגה (שבט)',
  'זבדיאל',
  'זוהר',
  'זיקים',
  'זיתן',
  'זכרון יעקב',
  'זכריה',
  'זמר',
  'זמרת',
  'זנוח',
  'זרועה',
  'זרזיר',
  'זרחיה',
  'ח\'ואלד',
  'ח\'ואלד (שבט)',
  'חבצלת השרון',
  'חבר',
  'חברון',
  'חגור',
  'חגי',
  'חגלה',
  'חד-נס',
  'חדיד',
  'חדרה',
  'חוג\'ייראת (ד\'הרה)',
  'חולדה',
  'חולון',
  'חולית',
  'חולתה',
  'חומש',
  'חוסן',
  'חוסנייה',
  'חופית',
  'חוקוק',
  'חורה',
  'חורפיש',
  'חורשים',
  'חזון',
  'חיבת ציון',
  'חיננית',
  'חיפה',
  'חירות',
  'חלוץ',
  'חלץ',
  'חמאם',
  'חמד',
  'חמדיה',
  'חמדת',
  'חמרה',
  'חניאל',
  'חניתה',
  'חנתון',
  'חספין',
  'חפץ חיים',
  'חפצי-בה',
  'חצב',
  'חצבה',
  'חצור הגלילית',
  'חצור-אשדוד',
  'חצרים',
  'חרב לאת',
  'חרוצים',
  'חריש',
  'חרמש',
  'חרשה',
  'חרשים',
  'חשמונאים',
  'טבריה',
  'טובא-זנגריה',
  'טורעאן',
  'טייבה',
  'טייבה (בעמק)',
  'טירה',
  'טירת יהודה',
  'טירת כרמל',
  'טירת צבי',
  'טל מנשה',
  'טל שחר',
  'טל-אל',
  'טללים',
  'טלמון',
  'טמרה',
  'טמרה (יזרעאל)',
  'טנא',
  'טפחות',
  'יאנוח-ג\'ת',
  'יבול',
  'יבנאל',
  'יבנה',
  'יגור',
  'יגל',
  'יד בנימין',
  'יד השמונה',
  'יד חנה',
  'יד מרדכי',
  'יד נתן',
  'יד רמבם',
  'ידידה',
  'יהוד-מונוסון',
  'יהל',
  'יובל',
  'יובלים',
  'יודפת',
  'יונדב',
  'יונתן',
  'יושיביה',
  'יזרעאל',
  'יחיעם',
  'יטבתה',
  'ייטב',
  'יכיני',
  'ינוב',
  'ינון',
  'יסוד המעלה',
  'יסודות',
  'יסעור',
  'יעד',
  'יעל',
  'יעף',
  'יערה',
  'יפיע',
  'יפית',
  'יפעת',
  'יפתח',
  'יצהר',
  'יציץ',
  'יקום',
  'יקיר',
  'יקנעם (מושבה)',
  'יקנעם עילית',
  'יראון',
  'ירדנה',
  'ירוחם',
  'ירושלים',
  'ירחיב',
  'ירכא',
  'ירקונה',
  'ישע',
  'ישעי',
  'ישרש',
  'יתד',
  'כאבול',
  'כאוכב אבו אל-היג\'א',
  'כברי',
  'כדורי',
  'כדים',
  'כדיתה',
  'כוכב השחר',
  'כוכב יאיר-צור יגאל',
  'כוכב יעקב',
  'כוכב מיכאל',
  'כורזים',
  'כחל',
  'כחלה',
  'כיסופים',
  'כישור',
  'כליל',
  'כלנית',
  'כמאנה',
  'כמהין',
  'כמון',
  'כנות',
  'כנף',
  'כנפי שחר',
  'כנרת (מושבה)',
  'כנרת (קבוצה)',
  'כסיפה',
  'כסלון',
  'כסרא-סמיע',
  'כעביה-טבאש-חג\'אג\'רה',
  'כפר אביב',
  'כפר אדומים',
  'כפר אוריה',
  'כפר אחים',
  'כפר ביאליק',
  'כפר בילו',
  'כפר בלום',
  'כפר בן נון',
  'כפר ברא',
  'כפר ברוך',
  'כפר גדעון',
  'כפר גלים',
  'כפר גליקסון',
  'כפר גלעדי',
  'כפר דניאל',
  'כפר האורנים',
  'כפר החורש',
  'כפר המכבי',
  'כפר הנגיד',
  'כפר הנוער הדתי',
  'כפר הנשיא',
  'כפר הס',
  'כפר הראה',
  'כפר הריף',
  'כפר ויתקין',
  'כפר ורבורג',
  'כפר ורדים',
  'כפר זוהרים',
  'כפר זיתים',
  'כפר חבד',
  'כפר חושן',
  'כפר חיטים',
  'כפר חיים',
  'כפר חנניה',
  'כפר חסידים א\'',
  'כפר חסידים ב\'',
  'כפר חרוב',
  'כפר טרומן',
  'כפר יאסיף',
  'כפר ידידיה',
  'כפר יהושע',
  'כפר יונה',
  'כפר יחזקאל',
  'כפר יעבץ',
  'כפר כמא',
  'כפר כנא',
  'כפר מונש',
  'כפר מימון',
  'כפר מלל',
  'כפר מנדא',
  'כפר מנחם',
  'כפר מסריק',
  'כפר מצר',
  'כפר מרדכי',
  'כפר נטר',
  'כפר סאלד',
  'כפר סבא',
  'כפר סילבר',
  'כפר סירקין',
  'כפר עבודה',
  'כפר עזה',
  'כפר עציון',
  'כפר פינס',
  'כפר קאסם',
  'כפר קיש',
  'כפר קרע',
  'כפר ראש הנקרה',
  'כפר רוזנואלד (זרעית)',
  'כפר רופין',
  'כפר רות',
  'כפר שמאי',
  'כפר שמואל',
  'כפר שמריהו',
  'כפר תבור',
  'כפר תפוח',
  'כרכום',
  'כרם ביבנה (ישיבה)',
  'כרם בן זמרה',
  'כרם בן שמן',
  'כרם מהרל',
  'כרם רעים',
  'כרם שלום',
  'כרמי יוסף',
  'כרמי צור',
  'כרמי קטיף',
  'כרמיאל',
  'כרמיה',
  'כרמים',
  'כרמל',
  'לבון',
  'לביא',
  'לבנים',
  'להב',
  'להבות הבשן',
  'להבות חביבה',
  'להבים',
  'לוד',
  'לוזית',
  'לוחמי הגיטאות',
  'לוטם',
  'לוטן',
  'לחי',
  'לימן',
  'לכיש',
  'לפיד',
  'לפידות',
  'לקיה',
  'לשם',
  'מאור',
  'מאיר שפיה',
  'מבוא ביתר',
  'מבוא דותן',
  'מבוא חורון',
  'מבוא חמה',
  'מבוא מודיעים',
  'מבואות ים',
  'מבואות יריחו',
  'מבועים',
  'מבטחים',
  'מבקיעים',
  'מבשרת ציון',
  'מג\'ד אל-כרום',
  'מג\'דל שמס',
  'מגאר',
  'מגדים',
  'מגדל',
  'מגדל העמק',
  'מגדל עוז',
  'מגדלים',
  'מגידו',
  'מגל',
  'מגן',
  'מגן שאול',
  'מגרון',
  'מגשימים',
  'מדרך עוז',
  'מדרשת בן גוריון',
  'מדרשת רופין',
  'מודיעין עילית',
  'מודיעין-מכבים-רעות',
  'מולדת',
  'מוצא עילית',
  'מוקייבלה',
  'מורן',
  'מורשת',
  'מזור',
  'מזכרת בתיה',
  'מזרע',
  'מזרעה',
  'מחולה',
  'מחנה הילה',
  'מחנה טלי',
  'מחנה יהודית',
  'מחנה יוכבד',
  'מחנה יפה',
  'מחנה יתיר',
  'מחנה מרים',
  'מחנה תל נוף',
  'מחניים',
  'מחסיה',
  'מטולה',
  'מטע',
  'מי עמי',
  'מיטב',
  'מיטל',
  'מייסר',
  'מיצר',
  'מירב',
  'מירון',
  'מישר',
  'מיתר',
  'מכורה',
  'מכחול',
  'מכמורת',
  'מכמנים',
  'מלאה',
  'מלאכי השלום',
  'מלילות',
  'מלכיה',
  'מנוחה',
  'מנוף',
  'מנות',
  'מנחמיה',
  'מנרה',
  'מנשית זבדה',
  'מסד',
  'מסדה',
  'מסילות',
  'מסילת ציון',
  'מסלול',
  'מסעדה',
  'מסעודין אל-עזאזמה',
  'מעברות',
  'מעגלים',
  'מעגן',
  'מעגן מיכאל',
  'מעוז',
  'מעוז חיים',
  'מעון',
  'מעונה',
  'מעיליא',
  'מעין ברוך',
  'מעין צבי',
  'מעלה אדומים',
  'מעלה אפרים',
  'מעלה גלבוע',
  'מעלה גמלא',
  'מעלה החמישה',
  'מעלה לבונה',
  'מעלה מכמש',
  'מעלה עירון',
  'מעלה עמוס',
  'מעלות-תרשיחא',
  'מענית',
  'מעש',
  'מפלסים',
  'מצובה',
  'מצוקי ארץ',
  'מצליח',
  'מצפה',
  'מצפה אביב',
  'מצפה אילן',
  'מצפה זיף',
  'מצפה יריחו',
  'מצפה נטופה',
  'מצפה רמון',
  'מצפה שלם',
  'מצר',
  'מקווה ישראל',
  'מרגליות',
  'מרום גולן',
  'מרחב עם',
  'מרחביה (מושב)',
  'מרחביה (קיבוץ)',
  'מרכז שפירא',
  'משאבי שדה',
  'משגב דב',
  'משגב עם',
  'משהד',
  'משואה',
  'משואות יצחק',
  'משכיות',
  'משמר איילון',
  'משמר דוד',
  'משמר הירדן',
  'משמר הנגב',
  'משמר העמק',
  'משמר השבעה',
  'משמר השרון',
  'משמר יהודה',
  'משמרות',
  'משמרת',
  'משען',
  'מתן',
  'מתת',
  'מתתיהו',
  'נאות גולן',
  'נאות הכיכר',
  'נאות מרדכי',
  'נאות סמדר',
  'נאעורה',
  'נבטים',
  'נגבה',
  'נגוהות',
  'נהורה',
  'נהלל',
  'נהריה',
  'נוב',
  'נוגה',
  'נוה צוף',
  'נווה',
  'נווה אבות',
  'נווה אור',
  'נווה אטיב',
  'נווה אילן',
  'נווה איתן',
  'נווה גדיד',
  'נווה דניאל',
  'נווה זוהר',
  'נווה זיו',
  'נווה חריף',
  'נווה ים',
  'נווה ימין',
  'נווה ירק',
  'נווה מבטח',
  'נווה מיכאל',
  'נווה שלום',
  'נועם',
  'נוף איילון',
  'נוף הגליל',
  'נופי פרת',
  'נופים',
  'נופית',
  'נופך',
  'נוקדים',
  'נורדיה',
  'נורית',
  'נזר שומרון',
  'נחושה',
  'נחל עוז',
  'נחלה',
  'נחליאל',
  'נחלים',
  'נחם',
  'נחף',
  'נחשולים',
  'נחשון',
  'נחשונים',
  'נטועה',
  'נטור',
  'נטע',
  'נטעים',
  'נטף',
  'ניין',
  'נילי',
  'ניצן',
  'ניצן ב\'',
  'ניצנה (קהילת חינוך)',
  'ניצני סיני',
  'ניצני עוז',
  'ניצנים',
  'ניר אליהו',
  'ניר בנים',
  'ניר גלים',
  'ניר דוד (תל עמל)',
  'ניר חן',
  'ניר יפה',
  'ניר יצחק',
  'ניר ישראל',
  'ניר משה',
  'ניר עוז',
  'ניר עם',
  'ניר עציון',
  'ניר עקיבא',
  'ניר צבי',
  'נירים',
  'נירית',
  'נמרוד',
  'נס הרים',
  'נס עמים',
  'נס ציונה',
  'נעורים',
  'נעלה',
  'נעמה',
  'נען',
  'נערן',
  'נצאצרה (שבט)',
  'נצר חזני',
  'נצר סרני',
  'נצרת',
  'נריה',
  'נשר',
  'נתיב הגדוד',
  'נתיב הלה',
  'נתיב העשרה',
  'נתיב השיירה',
  'נתיבות',
  'נתניה',
  'סאג\'ור',
  'סאסא',
  'סביון',
  'סגולה',
  'סואעד (חמירה)',
  'סואעד (כמאנה) (שבט)',
  'סולם',
  'סוסיה',
  'סופה',
  'סח\'נין',
  'סייד (שבט)',
  'סלמה',
  'סלעית',
  'סמר',
  'סנסנה',
  'סעד',
  'סעוה',
  'סער',
  'ספיר',
  'סתריה',
  'ע\'ג\'ר',
  'עבדון',
  'עברון',
  'עגור',
  'עדי',
  'עדי עד',
  'עדנים',
  'עוזה',
  'עוזייר',
  'עולש',
  'עומר',
  'עופר',
  'עופרה',
  'עוצם',
  'עוקבי (בנו עוקבה)',
  'עזוז',
  'עזר',
  'עזריאל',
  'עזריה',
  'עזריקם',
  'עטאוונה (שבט)',
  'עטרת',
  'עיבל',
  'עידן',
  'עיילבון',
  'עיינות',
  'עילוט',
  'עין איילה',
  'עין אל-אסד',
  'עין גב',
  'עין גדי',
  'עין דור',
  'עין הבשור',
  'עין הוד',
  'עין החורש',
  'עין המפרץ',
  'עין הנציב',
  'עין העמק',
  'עין השופט',
  'עין השלושה',
  'עין ורד',
  'עין זיוון',
  'עין חוד',
  'עין חצבה',
  'עין חרוד (איחוד)',
  'עין חרוד (מאוחד)',
  'עין יהב',
  'עין ייטב',
  'עין יעקב',
  'עין כרם-ביס חקלאי',
  'עין כרמל',
  'עין מאהל',
  'עין נקובא',
  'עין עירון',
  'עין צורים',
  'עין קנייא',
  'עין ראפה',
  'עין שמר',
  'עין שריד',
  'עין תמר',
  'עינת',
  'עיר אובות',
  'עכו',
  'עלומים',
  'עלי',
  'עלי זהב',
  'עלמה',
  'עלמון',
  'עמוקה',
  'עמיחי',
  'עמינדב',
  'עמיעד',
  'עמיעוז',
  'עמיקם',
  'עמיר',
  'עמנואל',
  'עמקה',
  'ענב',
  'עספיא',
  'עפולה',
  'עצמון שגב',
  'עראבה',
  'עראמשה',
  'ערב אל נעים',
  'ערד',
  'ערוגות',
  'ערערה',
  'ערערה-בנגב',
  'עשהאל',
  'עשרת',
  'עתלית',
  'עתניאל',
  'פארן',
  'פדואל',
  'פדויים',
  'פדיה',
  'פוריה - כפר עבודה',
  'פוריה - נווה עובד',
  'פוריה עילית',
  'פוריידיס',
  'פורת',
  'פטיש',
  'פלך',
  'פלמחים',
  'פני חבר',
  'פסגות',
  'פסוטה',
  'פעמי תשז',
  'פצאל',
  'פקיעין (בוקייעה)',
  'פקיעין חדשה',
  'פרדס חנה-כרכור',
  'פרדסיה',
  'פרוד',
  'פרזון',
  'פרי גן',
  'פרשים',
  'פתח תקווה',
  'פתחיה',
  'צאלים',
  'צביה',
  'צבעון',
  'צובה',
  'צוחר',
  'צופיה',
  'צופים',
  'צופית',
  'צופר',
  'צוקי ים',
  'צוקים',
  'צור הדסה',
  'צור יצחק',
  'צור משה',
  'צור נתן',
  'צוריאל',
  'צורית',
  'ציפורי',
  'צלפון',
  'צנדלה',
  'צפריה',
  'צפרירים',
  'צפת',
  'צרופה',
  'צרעה',
  'קבועה (שבט)',
  'קבוצת יבנה',
  'קדומים',
  'קדימה-צורן',
  'קדם ערבה',
  'קדמה',
  'קדמת צבי',
  'קדר',
  'קדרון',
  'קדרים',
  'קודייראת א-צאנע(שבט)',
  'קוואעין (שבט)',
  'קוממיות',
  'קורנית',
  'קטורה',
  'קיסריה',
  'קלחים',
  'קליה',
  'קלנסווה',
  'קלע',
  'קציר',
  'קצר א-סר',
  'קצרין',
  'קרית אונו',
  'קרית ארבע',
  'קרית אתא',
  'קרית ביאליק',
  'קרית גת',
  'קרית טבעון',
  'קרית ים',
  'קרית יערים',
  'קרית יערים(מוסד)',
  'קרית מוצקין',
  'קרית מלאכי',
  'קרית נטפים',
  'קרית ענבים',
  'קרית עקרון',
  'קרית שלמה',
  'קרית שמונה',
  'קרני שומרון',
  'קשת',
  'ראמה',
  'ראס אל-עין',
  'ראס עלי',
  'ראש העין',
  'ראש פינה',
  'ראש צורים',
  'ראשון לציון',
  'רבבה',
  'רבדים',
  'רביבים',
  'רביד',
  'רגבה',
  'רגבים',
  'רהט',
  'רווחה',
  'רוויה',
  'רוח מדבר',
  'רוחמה',
  'רומאנה',
  'רומת הייב',
  'רועי',
  'רותם',
  'רחוב',
  'רחובות',
  'רחלים',
  'ריחאניה',
  'ריחן',
  'ריינה',
  'רימונים',
  'רינתיה',
  'רכסים',
  'רם-און',
  'רמות',
  'רמות השבים',
  'רמות מאיר',
  'רמות מנשה',
  'רמות נפתלי',
  'רמלה',
  'רמת גן',
  'רמת דוד',
  'רמת הכובש',
  'רמת הנשיא טראמפ',
  'רמת השופט',
  'רמת השרון',
  'רמת יוחנן',
  'רמת ישי',
  'רמת מגשימים',
  'רמת צבי',
  'רמת רזיאל',
  'רמת רחל',
  'רנן',
  'רעים',
  'רעננה',
  'רקפת',
  'רשפון',
  'רשפים',
  'רתמים',
  'שא-נור',
  'שאר ישוב',
  'שבות רחל',
  'שבי דרום',
  'שבי ציון',
  'שבי שומרון',
  'שבלי - אום אל-גנם',
  'שגב-שלום',
  'שדה אילן',
  'שדה אליהו',
  'שדה אליעזר',
  'שדה בוקר',
  'שדה דוד',
  'שדה ורבורג',
  'שדה יואב',
  'שדה יעקב',
  'שדה יצחק',
  'שדה משה',
  'שדה נחום',
  'שדה נחמיה',
  'שדה ניצן',
  'שדה עוזיהו',
  'שדה צבי',
  'שדות ים',
  'שדות מיכה',
  'שדי אברהם',
  'שדי חמד',
  'שדי תרומות',
  'שדמה',
  'שדמות דבורה',
  'שדמות מחולה',
  'שדרות',
  'שואבה',
  'שובה',
  'שובל',
  'שוהם',
  'שומרה',
  'שומריה',
  'שוקדה',
  'שורש',
  'שורשים',
  'שושנת העמקים',
  'שזור',
  'שחר',
  'שחרות',
  'שחרית',
  'שיבולים',
  'שיזף',
  'שיטים',
  'שייח\' דנון',
  'שילה',
  'שילת',
  'שכניה',
  'שלווה',
  'שלווה במדבר',
  'שלוחות',
  'שלומי',
  'שלומית',
  'שמיר',
  'שמעה',
  'שמרת',
  'שמשית',
  'שני',
  'שניר',
  'שעב',
  'שעל',
  'שעלבים',
  'שער אפרים',
  'שער הגולן',
  'שער העמקים',
  'שער מנשה',
  'שער שומרון',
  'שפיים',
  'שפיר',
  'שפר',
  'שפרעם',
  'שקד',
  'שקף',
  'שרונה',
  'שריגים (לי-און)',
  'שריד',
  'שרשרת',
  'שתולה',
  'שתולים',
  'תאשור',
  'תדהר',
  'תובל',
  'תומר',
  'תושיה',
  'תימורים',
  'תירוש',
  'תל אביב - יפו',
  'תל יוסף',
  'תל יצחק',
  'תל מונד',
  'תל עדשים',
  'תל ציון',
  'תל קציר',
  'תל שבע',
  'תל תאומים',
  'תלם',
  'תלמי אליהו',
  'תלמי אלעזר',
  'תלמי בילו',
  'תלמי יוסף',
  'תלמי יחיאל',
  'תלמי יפה',
  'תלמים',
  'תמרה',
  'תמרת',
  'תנובות',
  'תעוז',
  'תפרח',
  'תקומה',
  'תקוע',
  'תראבין א-צאנע (שבט)',
  'תראבין א-צאנע(ישוב)',
  'תרום',
];
